import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Content, FlexboxGrid } from 'rsuite';
import {
  PAYMENT_PROVIDER,
  PAYMENT_METHODS,
} from '../../components/payment/constants';
import FlowPageLayout from '../../components/layout/FlowPageLayout';
import PortalHeader from '../../components/layout/PortalHeader';
import Payment from '../../components/payment/common/GenericPayment/Payment';
import { FLOWS_ENUM } from '../../common/enums';
import {
  selectPaymentID,
  selectSelectedPaymentMethod,
} from '../../store/slices/payment/paymentSelectors';
import { checkApplicationPaymentAsync } from '../../store/slices/payment/paymentThunk';
import {
  selectCurrentStep,
  selectFlowSteps,
} from '../../store/slices/flow/flowSelectors';
import { selectRenewApplicationNumber } from '../../store/slices/renew/renewSelectors';
import {
  setCurrentStep,
  setStepCompletedStatus,
} from '../../store/slices/flow/flowSlice';
import { selectConfigSettings } from '../../store/slices/config/configSelectors';
import usePlaceNavigate from '../../hooks/usePlaceNavigate';
import { useLocation } from 'react-router-dom';

const RenewPayment = () => {
  const placeNavigate = usePlaceNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const paymentID = useSelector(selectPaymentID);
  const selectedCurrentStep = useSelector(selectCurrentStep);
  const selectedPaymentMethod = useSelector(selectSelectedPaymentMethod);
  const applicationNumber = useSelector(selectRenewApplicationNumber);
  const settings = useSelector(selectConfigSettings);
  const provider = settings.paymentProcessor;

  const redirectUrl = useMemo(() => {
    if (selectedPaymentMethod === PAYMENT_METHODS.CHECK) {
      return '/renew-success';
    }
    switch (provider) {
      case PAYMENT_PROVIDER.GENERIC:
      case PAYMENT_PROVIDER.XPRESS_BILL_PAY:
        return '/payment-redirect';
      case PAYMENT_PROVIDER.Stripe:
      default:
        return '/renew-success';
    }
  }, [provider, selectedPaymentMethod]);

  const onSuccess = useCallback(
    (omitCheck) => {
      if (!omitCheck) {
        dispatch(
          checkApplicationPaymentAsync(
            paymentID,
            placeNavigate(redirectUrl, {
              state: { isBulkRenew: location?.state?.isBulkRenew ?? false },
            })
          )
        );
      } else {
        placeNavigate(redirectUrl);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [paymentID, dispatch, placeNavigate, redirectUrl]
  );

  return (
    <FlexboxGrid justify="space-between">
      <FlexboxGrid.Item colspan={24}>
        <PortalHeader flow={FLOWS_ENUM.RENEW_LICENSE} />
      </FlexboxGrid.Item>
      <FlexboxGrid.Item colspan={24}>
        <Content>
          <FlowPageLayout
            title="Payment"
            hideNextButton={true}
            onBack={() => {
              dispatch(
                setStepCompletedStatus({
                  stepNum: selectedCurrentStep,
                  status: false,
                })
              );
              dispatch(setCurrentStep(selectedCurrentStep - 1));
              placeNavigate(
                location?.state?.isBulkRenew === true
                  ? '/bulk-renew-confirmation'
                  : '/renew-confirmation'
              );
            }}
            flowProgressStepsSelector={selectFlowSteps}
            currentStep={selectedCurrentStep}
          >
            <Payment
              flow={FLOWS_ENUM.APPLY_LICENSE}
              onSuccess={onSuccess}
              permitIdentifier={applicationNumber}
              singlePaymentButton={
                provider === PAYMENT_PROVIDER.GENERIC ||
                provider === PAYMENT_PROVIDER.XPRESS_BILL_PAY
              }
            />
          </FlowPageLayout>
        </Content>
      </FlexboxGrid.Item>
    </FlexboxGrid>
  );
};

export default RenewPayment;
